import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getTentHouseProviderServices } from '../../../Apiservices/Auth/servers/fetchProviderData';
import { CalendarDropdown } from '../redesigncatering/CalendarDropdown';
import { format } from 'date-fns';
import { uploadImage } from '../../../Apiservices/Auth/servers/venueServices';
import { ImageUploadSection } from '../ImageUploadSection';
import styles from './TentPackageService.module.css';

const MAX_RECOMMENDED_EVENTS = 3;

const TentPackageService = ({ mode, eventId, packageData, onSave, onCancel, events = [], toast }) => {
  const dragHandleRef = useRef(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDiscountCalendar, setShowDiscountCalendar] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Image and status states
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [existingImageIds, setExistingImageIds] = useState([]);
  const [uploadedImageIds, setUploadedImageIds] = useState([]);
  const [imagesModified, setImagesModified] = useState(false);

  // Modal states for item selection
  const [showSelectItemsModal, setShowSelectItemsModal] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  // Modal states for event selection
  const [showSelectEventsModal, setShowSelectEventsModal] = useState(false);
  const [modalEventSearch, setModalEventSearch] = useState('');
  const [modalSelectedEventIds, setModalSelectedEventIds] = useState([]);

  const [formData, setFormData] = useState({
    package_name: '',
    description: '',
    people_capacity: '',
    regular_price: '',
    offered_price: '',
    offered_valid_till: '',
    is_active: true,
    package_items: [],
    recommended_event_ids: []
  });

  // Filter services for the modal based on search term
  const filteredModalServices = useMemo(() => {
    if (!modalSearchTerm.trim()) return services;
    return services.filter(service =>
      service.service_name.toLowerCase().includes(modalSearchTerm.toLowerCase())
    );
  }, [services, modalSearchTerm]);

  // Flatten events for search and global operations
  const flatEvents = useMemo(() => {
    return events.reduce((acc, cat) => {
      if (cat.events && Array.isArray(cat.events)) {
        return [...acc, ...cat.events];
      }
      return acc;
    }, []);
  }, [events]);

  // Filter flat events based on search term for the modal
  const filteredEvents = useMemo(() => {
    if (!modalEventSearch.trim()) return [];
    return flatEvents.filter(event =>
      event.name.toLowerCase().includes(modalEventSearch.toLowerCase())
    );
  }, [flatEvents, modalEventSearch]);

  // Sort and filter grouped events when search is empty
  const groupedEvents = useMemo(() => {
    if (modalEventSearch.trim()) return [];
    const sortedCategories = [...events].sort((a, b) => (a.order || 0) - (b.order || 0));
    return sortedCategories.filter(cat => cat.events && cat.events.length > 0);
  }, [events, modalEventSearch]);

  // Get selected events for display
  const selectedEvents = useMemo(() => {
    return flatEvents.filter(event =>
      formData.recommended_event_ids.includes(event.id)
    );
  }, [flatEvents, formData.recommended_event_ids]);

  const eventLimitReached = selectedEvents.length >= MAX_RECOMMENDED_EVENTS;

  // Load services and set initial data
  useEffect(() => {
    loadServices();
    if (mode === 'edit' && packageData) {
      const eventIds = packageData.recommendedEvents
        ? packageData.recommendedEvents.map(event => event.id)
        : [];

      if (packageData.package_items && Array.isArray(packageData.package_items)) {
        const transformedPackageItems = packageData.package_items.map(item => {
          // USE THE SERVICE OBJECT ID, NOT THE FOREIGN KEY (because foreign key is wrong)
          const correctServiceId = item.tent_house_service?.id || item.tent_house_service_id;
          return {
            tent_house_service_id: Number(correctServiceId),
            item_qty: item.item_qty
          };
        });

        // Safe extraction of existing images (assuming API might return images, gallery, or image_ids)
        const getExistingImages = (pkg) => {
          if (pkg.image_ids && Array.isArray(pkg.image_ids)) return pkg.image_ids;
          if (pkg.gallery && Array.isArray(pkg.gallery)) return pkg.gallery.map(img => img.id || img.fileId || img);
          if (pkg.images && Array.isArray(pkg.images)) return pkg.images.map(img => img.id || img.fileId || img);
          return [];
        };
        const initialImages = getExistingImages(packageData);
        setExistingImageIds(initialImages);

        setFormData({
          package_name: packageData.package_name || '',
          description: packageData.description || '',
          people_capacity: packageData.people_capacity || '',
          regular_price: packageData.regular_price || '',
          offered_price: packageData.offered_price || '',
          offered_valid_till: packageData.offered_valid_till || '',
          is_active: packageData.is_active !== undefined ? packageData.is_active : true,
          package_items: transformedPackageItems,
          recommended_event_ids: eventIds,
        });

        // Set form data with transformed items

      } else {
        setExistingImageIds([]);
        setFormData({
          package_name: packageData.package_name || '',
          description: packageData.description || '',
          people_capacity: packageData.people_capacity || '',
          regular_price: packageData.regular_price || '',
          offered_price: packageData.offered_price || '',
          offered_valid_till: packageData.offered_valid_till || '',
          is_active: packageData.is_active !== undefined ? packageData.is_active : true,
          package_items: [],
          recommended_event_ids: eventIds,
        });
      }
    }
  }, [mode, packageData]);

  // Image Upload Handlers
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploadingImages(true);
    setImagesModified(true);
    
    try {
      const newImageIds = [];
      for (const file of files) {
        const fileId = await uploadImage(file);
        if (fileId) {
          newImageIds.push(fileId);
        }
      }
      setUploadedImageIds(prev => [...prev, ...newImageIds]);
    } catch (error) {
      console.error('Failed to upload images:', error);
      toast?.current?.show({ severity: 'error', summary: 'Upload Failed', detail: 'Failed to upload one or more images. Please try again.', life: 4000 });
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleRemoveImage = (id, isExisting) => {
    setImagesModified(true);
    if (isExisting) {
      setExistingImageIds(prev => prev.filter(imageId => imageId !== id));
    } else {
      setUploadedImageIds(prev => prev.filter(imageId => imageId !== id));
    }
  };

  const loadServices = async () => {
    try {
      const servicesData = await getTentHouseProviderServices();
      setServices(servicesData || []);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOpenModal = () => {
    const existingIds = formData.package_items.map(item => Number(item.tent_house_service_id));
    setSelectedServiceIds(existingIds);
    setModalSearchTerm('');
    setShowSelectItemsModal(true);
  };

  const handleToggleServiceSelection = (id, checked) => {
    if (checked) {
      setSelectedServiceIds(prev => [...prev, id]);
    } else {
      setSelectedServiceIds(prev => prev.filter(serviceId => serviceId !== id));
    }
  };

  const handleAddSelectedItems = () => {
    const newPackageItems = selectedServiceIds.map(id => {
      const existing = formData.package_items.find(item => Number(item.tent_house_service_id) === id);
      if (existing) {
        return existing;
      }
      return {
        tent_house_service_id: id,
        item_qty: "1.00"
      };
    });
    
    setFormData(prev => ({
      ...prev,
      package_items: newPackageItems
    }));
    setShowSelectItemsModal(false);
  };

  // Event handlers
  const handleOpenEventsModal = () => {
    setModalSelectedEventIds([...formData.recommended_event_ids]);
    setModalEventSearch('');
    setShowSelectEventsModal(true);
  };

  const handleToggleEventSelection = (id, checked) => {
    if (checked) {
      if (modalSelectedEventIds.length >= MAX_RECOMMENDED_EVENTS) return;
      setModalSelectedEventIds(prev => [...prev, id]);
    } else {
      setModalSelectedEventIds(prev => prev.filter(eventId => eventId !== id));
    }
  };

  const handleAddSelectedEvents = () => {
    setFormData(prev => ({
      ...prev,
      recommended_event_ids: modalSelectedEventIds
    }));
    setShowSelectEventsModal(false);
  };

  const handleRemoveEvent = (eventId) => {
    setFormData(prev => ({
      ...prev,
      recommended_event_ids: prev.recommended_event_ids.filter(id => id !== eventId)
    }));
  };

  // Item remove and quantity change logic

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      package_items: prev.package_items.filter((_, i) => i !== index)
    }));
  };

  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragEnter = (e, targetIndex) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === targetIndex) return;
    
    setFormData(prev => {
      const newItems = [...prev.package_items];
      const draggedItem = newItems[draggedItemIndex];
      newItems.splice(draggedItemIndex, 1);
      newItems.splice(targetIndex, 0, draggedItem);
      return { ...prev, package_items: newItems };
    });
    setDraggedItemIndex(targetIndex);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  const handleItemQtyChange = (index, value) => {
    const qty = parseFloat(value) || 1;
    if (qty < 1) return;

    const formattedQty = qty.toFixed(2);

    setFormData(prev => ({
      ...prev,
      package_items: prev.package_items.map((item, i) =>
        i === index ? { ...item, item_qty: formattedQty } : item
      )
    }));
  };

  const handleOfferPriceChange = (value) => {
    const price = parseFloat(value) || '';
    const regularPrice = parseFloat(formData.regular_price);

    if (price !== '' && regularPrice && price > regularPrice) {
      // Don't update — keep the value but show error via state
      setFormData(prev => ({ ...prev, offered_price: price }));
    } else {
      setFormData(prev => ({ ...prev, offered_price: price }));
    }

    if (price > 0) {
      setShowDiscountCalendar(true);
    } else {
      setShowDiscountCalendar(false);
      setFormData(prev => ({ ...prev, offered_valid_till: '' }));
    }
  };

  const getServiceName = (serviceId) => {
    const id = Number(serviceId);

    if (mode === 'edit' && packageData?.package_items) {
      const item = packageData.package_items.find(
        item =>
          Number(item.tent_house_service_id) === id ||
          Number(item.tent_house_service?.id) === id
      );
      if (item?.tent_house_service?.service_name) {
        return item.tent_house_service.service_name;
      }
    }

    const service = services.find(s => Number(s.id) === id);
    return service ? service.service_name : `Unknown Service (ID: ${id})`;
  };

  const formatInIST = (dateString) => {
    if (dateString && typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{2}$/)) {
      return dateString;
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);

    return `${day}/${month}/${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.package_name.trim()) {
        alert('Package name is required');
        return;
      }

      const validPackageItems = formData.package_items.filter(item => {
        const serviceId = Number(item.tent_house_service_id);
        const quantity = parseFloat(item.item_qty);
        return serviceId && serviceId > 0 && quantity > 0;
      });

      if (validPackageItems.length === 0) {
        alert('At least one package item is required');
        setShowSelectItemsModal(true);
        return;
      }

      if (formData.offered_price && formData.offered_price > 0) {
        const regularPrice = parseFloat(formData.regular_price);
        const offeredPrice = parseFloat(formData.offered_price);

        if (regularPrice && offeredPrice > regularPrice) {
          toast?.current?.show({ severity: 'warn', summary: 'Invalid Price', detail: 'Offered price cannot be higher than the regular price.', life: 4000 });
          return;
        }

        if (!formData.offered_valid_till) {
          toast?.current?.show({ severity: 'warn', summary: 'Missing Date', detail: 'Please select an offer valid date.', life: 4000 });
          return;
        }

        const offerDate = new Date(formData.offered_valid_till);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (offerDate <= today) {
          toast?.current?.show({ severity: 'warn', summary: 'Invalid Date', detail: 'Offer valid date must be a future date.', life: 4000 });
          return;
        }
      }

      const dataToSend = {
        ...formData,
        package_items: validPackageItems.map((item, index) => ({
          tent_house_service_id: Number(item.tent_house_service_id),
          item_qty: item.item_qty,
          sequence: index + 1
        })),
        people_capacity: formData.people_capacity ? Number(formData.people_capacity) : null,
        regular_price: formData.regular_price ? parseFloat(formData.regular_price).toFixed(2) : null,
        offered_price: formData.offered_price ? parseFloat(formData.offered_price).toFixed(2) : null,
        recommended_event_ids: formData.recommended_event_ids.map(id => Number(id)),
        is_active: formData.is_active
      };

      // Handle images based on mode and modification status
      if (mode === 'create') {
        dataToSend.image_ids = [...existingImageIds, ...uploadedImageIds];
      } else if (mode === 'edit' && imagesModified) {
        dataToSend.image_ids = [...existingImageIds, ...uploadedImageIds];
      }

      await onSave(dataToSend);
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Error saving package. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.packageFormContainer}>
      <form onSubmit={handleSubmit} className={styles.packageForm}>
        <div className={styles.formHeader}>
          <h2>{mode === 'create' ? 'Create Your Package' : 'Edit Package'}</h2>
          
          {/* Is Active Toggle */}
          <div className={styles.activeToggleWrapper} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label className={styles.label} style={{ marginBottom: 0 }}>Active Package</label>
            <input 
              type="checkbox" 
              checked={formData.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </div>
        </div>

        {/* Image Upload Section */}
        <div className={styles.formGroup}>
          <ImageUploadSection 
            existingImageIds={existingImageIds}
            uploadedImageIds={uploadedImageIds}
            onImageUpload={handleImageUpload}
            onRemoveImage={handleRemoveImage}
            isUploading={isUploadingImages}
            label="Package Images"
            maxImages={10}
          />
        </div>

        {/* Package Name */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Package Name</label>
          <div className={styles.inputWithClear}>
            <input
              type="text"
              value={formData.package_name}
              onChange={(e) => handleInputChange('package_name', e.target.value)}
              className={styles.input}
              placeholder="Enter Package Name..."
              required
            />
            {formData.package_name && (
              <button
                type="button"
                onClick={() => handleInputChange('package_name', '')}
                className={styles.clearButton}
                title="Clear package name"
              >
                <i className="pi pi-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Items */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Items</label>

          <button
            type="button"
            className={styles.addItemsButton}
            onClick={handleOpenModal}
          >
            <i className="pi pi-plus"></i>
            Add Items
            {formData.package_items.length > 0 && (
              <span className={styles.addItemsCount}>{formData.package_items.length}</span>
            )}
          </button>

          {formData.package_items.length > 0 && (
            <div className={styles.addedItemsSection}>
              <div className={styles.sectionHeader}>
                <h3>Package Items ({formData.package_items.length})</h3>
              </div>

              <div className={styles.itemsList}>
                {formData.package_items.map((item, index) => {
                  const serviceName = getServiceName(item.tent_house_service_id);
                  return (
                    <div 
                      key={item.tent_house_service_id + '-' + index} 
                      className={styles.addedItemRow}
                      draggable
                      onDragStart={(e) => {
                        if (dragHandleRef.current !== index) {
                          e.preventDefault();
                          return;
                        }
                        handleDragStart(e, index);
                      }}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnd={handleDragEnd}
                      style={{ 
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        boxShadow: draggedItemIndex === index ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                        background: draggedItemIndex === index ? '#f8f9fa' : 'white',
                        position: draggedItemIndex === index ? 'relative' : 'static',
                        zIndex: draggedItemIndex === index ? 10 : 1
                      }}
                    >
                      <div className={styles.itemInfo} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className={styles.serviceName}>{serviceName}</span>
                        {serviceName.startsWith('Unknown Service') && (
                          <span className={styles.serviceId}>
                            (ID: {item.tent_house_service_id})
                          </span>
                        )}
                      </div>

                      <div className={styles.itemActions} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className={styles.removeItemBtn}
                          title="Remove item"
                        >
                          <i className="pi pi-trash"></i>
                        </button>
                        <i 
                          className="pi pi-bars" 
                          style={{ color: '#888', cursor: 'grab', fontSize: '1.2rem' }}
                          onMouseDown={() => { dragHandleRef.current = index; }}
                          onMouseUp={() => { dragHandleRef.current = null; }}
                        ></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className={styles.textarea}
            placeholder="Enter package description"
            rows="3"
          />
        </div>

        {/* Price / Offer Price side by side */}
        <div className={styles.priceRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Price</label>
            <div className={styles.inputWithClear}>
              <input
                type="number"
                value={formData.regular_price}
                onChange={(e) => handleInputChange('regular_price', parseFloat(e.target.value) || '')}
                className={styles.input}
                placeholder="Enter Price"
                min="0"
                step="0.01"
              />
              {formData.regular_price && (
                <button
                  type="button"
                  onClick={() => handleInputChange('regular_price', '')}
                  className={styles.clearButton}
                  title="Clear regular price"
                >
                  <i className="pi pi-times"></i>
                </button>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Offer Price</label>
            <div className={styles.inputWithClear}>
              <input
                type="number"
                value={formData.offered_price}
                onChange={(e) => handleOfferPriceChange(e.target.value)}
                className={styles.input}
                placeholder="Enter Offer Price"
                min="0"
                step="0.01"
              />
              {formData.offered_price && (
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('offered_price', '');
                    handleInputChange('offered_valid_till', '');
                    setShowDiscountCalendar(false);
                  }}
                  className={styles.clearButton}
                  title="Clear offered price"
                >
                  <i className="pi pi-times"></i>
                </button>
              )}
            </div>
            {formData.offered_price && parseFloat(formData.offered_price) > parseFloat(formData.regular_price) && formData.regular_price && (
              <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                ⚠ Offered price cannot be higher than the regular price (₹{formData.regular_price})
              </span>
            )}

            {formData.offered_price && formData.offered_price > 0 && (
              <div className={styles.offerDateContainer}>
                <label className={styles.sublabel}>Offer Valid Till</label>
                <div className={styles.dateDisplayContainer}>
                  <span className={styles.dateDisplay}>
                    {formData.offered_valid_till
                      ? formatInIST(formData.offered_valid_till)
                      : 'Not set'}
                  </span>
                  <div className={styles.dateActions}>
                    <button
                      type="button"
                      className={styles.calendarButton}
                      onClick={() => setShowDiscountCalendar(!showDiscountCalendar)}
                    >
                      <i className="pi pi-calendar"></i>
                    </button>
                  </div>
                </div>

                {showDiscountCalendar && (
                  <div className={styles.calendarDropdownWrapper}>
                    <CalendarDropdown
                      onDateSelect={(isoDateString) => {
                        handleInputChange('offered_valid_till', isoDateString);
                        setShowDiscountCalendar(false);
                      }}
                      selectedDate={
                        formData.offered_valid_till
                          ? format(new Date(formData.offered_valid_till), 'dd/MM/yy')
                          : null
                      }
                      onClose={() => setShowDiscountCalendar(false)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Number Of People */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Number Of People</label>
          <input
            type="number"
            value={formData.people_capacity}
            onChange={(e) => handleInputChange('people_capacity', e.target.value)}
            className={styles.input}
            placeholder="Enter Number Of People"
            min="1"
          />
        </div>

        {/* Suitable Events - capped at 3 */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Suitable Events</label>

          <div 
            className={styles.eventInputWrapper}
            onClick={handleOpenEventsModal}
          >
            <input
              type="text"
              readOnly
              className={styles.searchInput}
              placeholder="Select up to 3 events"
              value={selectedEvents.length > 0 ? selectedEvents.map(e => e.name).join(', ') : ''}
            />
          </div>

          {selectedEvents.length > 0 && (
            <div className={styles.eventsTagsContainer}>
                {selectedEvents.map(event => (
                  <div key={event.id} className={styles.eventTag}>
                    <span className={styles.tagContent}>
                      <span className={styles.eventName}>{event.name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveEvent(event.id)}
                      className={styles.removeTagBtn}
                      title="Remove this event"
                    >
                      <i className="pi pi-times"></i>
                    </button>
                  </div>
                ))}
              </div>
          )}
        </div>

        <div className={styles.formActions}>
          <button type="button" onClick={onCancel} className={styles.cancelButton}>
            Cancel
          </button>
          <button type="submit" disabled={loading} className={styles.saveButton}>
            {loading ? 'Saving...' : mode === 'create' ? 'Create Package Item' : 'Update Package Item'}
          </button>
        </div>
      </form>

      {/* Select Events Modal */}
      {showSelectEventsModal && createPortal(
        <div
          className={styles.modalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowSelectEventsModal(false);
            }
          }}
        >
          <div className={`${styles.modalContent} ${styles.eventsModalContent}`}>
            <div className={styles.modalHeader}>
              <h2>Select up to 3 Events</h2>
              <button 
                type="button" 
                className={styles.closeModalBtn}
                onClick={() => setShowSelectEventsModal(false)}
              >
                <i className="pi pi-times"></i>
              </button>
            </div>
            
            <div className={styles.modalSearchContainer}>
              <i className="pi pi-search"></i>
              <input 
                type="text" 
                placeholder="Type to search events..." 
                value={modalEventSearch}
                onChange={(e) => setModalEventSearch(e.target.value)}
                className={styles.modalSearchInput}
              />
            </div>
            
            <div className={styles.modalList}>
              {modalEventSearch.trim() ? (
                filteredEvents.length > 0 ? (
                  filteredEvents.map(event => {
                    const isChecked = modalSelectedEventIds.includes(event.id);
                    const isMaxReached = modalSelectedEventIds.length >= MAX_RECOMMENDED_EVENTS && !isChecked;
                    
                    return (
                      <label key={event.id} className={`${styles.modalListItem} ${isMaxReached ? styles.disabledItem : ''}`}>
                        <div className={styles.checkboxWrapper}>
                          <input
                            type="checkbox"
                            className={styles.customCheckbox}
                            checked={isChecked}
                            onChange={(e) => handleToggleEventSelection(event.id, e.target.checked)}
                            disabled={isMaxReached}
                          />
                          <div className={styles.checkmark}></div>
                        </div>
                        <div className={`${styles.modalServiceName} ${isChecked ? styles.activeText : ''}`}>
                          {event.name}
                          {event.description && (
                            <span className={styles.eventDescription}> - {event.description}</span>
                          )}
                        </div>
                      </label>
                    );
                  })
                ) : (
                  <div className={styles.noResultsModal}>No events found for "{modalEventSearch}"</div>
                )
              ) : (
                groupedEvents.length > 0 ? (
                  groupedEvents.map((category, index) => (
                    <div key={category.id} style={{ 
                      paddingBottom: '15px', 
                      marginBottom: '15px', 
                      borderBottom: index < groupedEvents.length - 1 ? '1px solid #ccc' : 'none' 
                    }}>
                      <div style={{ fontWeight: 'bold', fontSize: '1.1em', marginBottom: '8px' }}>
                        {category.name}
                      </div>
                      <div>
                        {category.events.map(event => {
                          const isChecked = modalSelectedEventIds.includes(event.id);
                          const isMaxReached = modalSelectedEventIds.length >= MAX_RECOMMENDED_EVENTS && !isChecked;
                          
                          return (
                            <label key={event.id} className={`${styles.modalListItem} ${isMaxReached ? styles.disabledItem : ''}`}>
                              <div className={styles.checkboxWrapper}>
                                <input
                                  type="checkbox"
                                  className={styles.customCheckbox}
                                  checked={isChecked}
                                  onChange={(e) => handleToggleEventSelection(event.id, e.target.checked)}
                                  disabled={isMaxReached}
                                />
                                <div className={styles.checkmark}></div>
                              </div>
                              <div className={`${styles.modalServiceName} ${isChecked ? styles.activeText : ''}`}>
                                {event.name}
                                {event.description && (
                                  <span className={styles.eventDescription}> - {event.description}</span>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.noResultsModal}>No events found</div>
                )
              )}
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                type="button" 
                className={styles.addSelectedButton}
                onClick={handleAddSelectedEvents}
              >
                Save Selected ({modalSelectedEventIds.length}/{MAX_RECOMMENDED_EVENTS})
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Select Items Modal Overlay */}
      {showSelectItemsModal && createPortal(
        <div
          className={styles.modalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowSelectItemsModal(false);
            }
          }}
        >
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <button 
                type="button" 
                onClick={() => setShowSelectItemsModal(false)} 
                className={styles.backButton}
              >
                <i className="pi pi-chevron-left"></i>
              </button>
              <h3>Select Items</h3>
            </div>
            
            <div className={styles.modalSearchContainer}>
              <div className={styles.modalSearchWrapper}>
                <i className={`pi pi-search ${styles.searchIcon}`}></i>
                <input 
                  type="text" 
                  placeholder="Search for an item..." 
                  value={modalSearchTerm} 
                  onChange={(e) => setModalSearchTerm(e.target.value)} 
                  className={styles.modalSearchInput}
                />
              </div>
            </div>

            <div className={styles.modalList}>
              {filteredModalServices.map(service => (
                <label key={service.id} className={styles.modalListItem}>
                  <div className={styles.checkboxWrapper}>
                    <input 
                      type="checkbox" 
                      checked={selectedServiceIds.includes(service.id)}
                      onChange={(e) => handleToggleServiceSelection(service.id, e.target.checked)}
                      className={styles.customCheckbox}
                    />
                    <span className={styles.checkmark}></span>
                  </div>
                  <span className={styles.modalServiceName}>{service.service_name}</span>
                </label>
              ))}
              {filteredModalServices.length === 0 && (
                <div className={styles.noResultsModal}>No items match your search.</div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button 
                type="button" 
                onClick={handleAddSelectedItems}
                className={styles.addSelectedButton}
              >
                <i className="pi pi-check"></i> Add Selected Items
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TentPackageService;